interface CheckVisibilityOptions {
    /** true to check if the element content-visibility property has (or inherits) the value auto, and it is currently skipping its rendering. false by default. */
    contentVisibilityAuto?: boolean;
    /** true to check if the element opacity property has (or inherits) a value of 0. false by default. */
    opacityProperty?: boolean;
    /** true to check if the element is invisible due to the value of its visibility property. false by default. */
    visibilityProperty?: boolean;
    /** A historic alias for opacityProperty*/
    checkOpacity?: boolean;
    /** A historic alias for visibilityProperty */
    checkVisibilityCSS?: boolean;
}

/**
 * provides a set of simple checks for whether an element is potentially "visible", not necessarily in viewport
 * https://developer.mozilla.org/en-US/docs/Web/API/Element/checkVisibility
 * https://drafts.csswg.org/cssom-view-1/#dom-element-checkvisibility
 * @param element
 * @param options
 * @returns boolean indicating whether the element is considered visible
 */
export const checkVisibility = (node: HTMLElement, options: CheckVisibilityOptions = {}): boolean => {
    // Step 1: If this does not have an associated box, return false.
    if (node.getClientRects().length === 0) return false;

    // Step 2: If an ancestor of this in the flat tree has content-visibility: hidden, return false.
    let ancestor: HTMLElement | null = node.parentElement;
    while (ancestor) {
        if (getComputedStyle(ancestor).getPropertyValue('content-visibility') === 'hidden') return false;
        ancestor = ancestor.parentElement;
    }

    // Step 3: If either the opacityProperty or the checkOpacity dictionary members of options are true, and this, or an ancestor of this in the flat tree, has a computed opacity value of 0, return false.
    if (options.opacityProperty || options.checkOpacity) {
        let element: HTMLElement | null = node;
        while (element) {
            if (parseFloat(getComputedStyle(element).opacity) === 0) return false;
            element = element.parentElement;
        }
    }

    // Step 4: If either the visibilityProperty or the checkVisibilityCSS dictionary members of options are true, and this is invisible, return false.
    if (options.visibilityProperty || options.checkVisibilityCSS) {
        const visibility = getComputedStyle(node).visibility;
        if (visibility === 'hidden' || visibility === 'collapse') return false;
    }

    // Step 5: If the contentVisibilityAuto dictionary member of options is true and an ancestor of this in the flat tree skips its contents due to content-visibility: auto, return false.
    if (options.contentVisibilityAuto) {
        let element: HTMLElement | null = node.parentElement;
        while (element) {
            // TODO: This doesn't fully cover the spec implementation of "an ancestor skips its contents" but the spec is vague on how to determine that. Not relevant for our use case for now, will come back to it later
            if (getComputedStyle(element).getPropertyValue('content-visibility') === 'auto') return false;
            element = element.parentElement;
        }
    }

    return true;
};
